import { Box, Grid, Skeleton } from "@mui/material";
import PropTypes from "prop-types";
import React from "react";
import { FLOOR_PLAN_PREVIEW_MAX_SCALE } from "../../constants/floorPlanPreview";
import {
  BENTO_SURFACE_BORDER_RADIUS,
  CARD_MAX_WIDTH,
} from "../UploadStates/uploadCardLayout";

/** Matches `FloorPlanCard` preview well + outer shell. */
const CARD_BORDER = "1px solid rgba(0,0,0,0.08)";
const CARD_BG = "#FFFFFF";
const PREVIEW_BG = "#F5F5F7";
/** Shimmer bones: visible on preview vs footer (aligned with card grays). */
const SKELETON_BONE_PREVIEW = "#E4E4E7";
const SKELETON_BONE_FOOTER = "#E8E8EA";

/** xs page padding = `1.25 * 8` = 10px; sm page padding = `2 * 8` = 16px — matches `MobileFloorPlanSlider`. */
const PAGE_PADDING_XS = "10px";
const PAGE_PADDING_SM = "16px";

const SCROLL_INSET_XS = `max(${PAGE_PADDING_XS}, calc((100% - ${CARD_MAX_WIDTH}px) / 2))`;
const SCROLL_INSET_SM = `max(${PAGE_PADDING_SM}, calc((100% - ${CARD_MAX_WIDTH}px) / 2))`;

const CARD_GAP_PX = 12;

const skeletonWaveSx = (boneColor) => ({
  bgcolor: boneColor,
});

/**
 * Shared shell: same border radius, border, and white surface as `FloorPlanCard`.
 */
function FloorPlanCardSkeletonShell({ children, sx = {} }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        minWidth: 0,
        flex: 1,
        border: CARD_BORDER,
        p: 0,
        borderRadius: BENTO_SURFACE_BORDER_RADIUS,
        bgcolor: CARD_BG,
        overflow: "hidden",
        boxSizing: "border-box",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

FloorPlanCardSkeletonShell.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
};

/**
 * Preview block: 1:1 gray field like `FloorPlanCard` `.floor-plan-card-preview`.
 */
function SkeletonPreviewBlock() {
  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: PREVIEW_BG,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        aspectRatio: "1 / 1",
        minHeight: 0,
        position: "relative",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Skeleton
        variant="rectangular"
        animation="wave"
        sx={{
          width: FLOOR_PLAN_PREVIEW_MAX_SCALE,
          height: FLOOR_PLAN_PREVIEW_MAX_SCALE,
          maxWidth: "100%",
          maxHeight: "100%",
          borderRadius: "2px",
          ...skeletonWaveSx(SKELETON_BONE_PREVIEW),
        }}
      />
    </Box>
  );
}

/**
 * Footer row: `pl: 10px`, `pr: 1`, `pt`/`pb: 1` + 36px options slot — matches `FloorPlanCard`.
 */
function SkeletonFooterBlock() {
  return (
    <Box
      sx={{
        pl: "10px",
        pr: 1,
        pt: 1,
        pb: 1,
        width: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-end",
        gap: 0.5,
        boxSizing: "border-box",
        bgcolor: CARD_BG,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.25 }}>
        <Skeleton
          variant="rectangular"
          animation="wave"
          width="78%"
          height={18}
          sx={{ borderRadius: "4px", maxWidth: "100%", ...skeletonWaveSx(SKELETON_BONE_FOOTER) }}
        />
        <Skeleton
          variant="rectangular"
          animation="wave"
          width="55%"
          height={14}
          sx={{ borderRadius: "4px", maxWidth: "100%", ...skeletonWaveSx(SKELETON_BONE_FOOTER) }}
        />
      </Box>
      <Skeleton
        variant="rectangular"
        animation="wave"
        width={36}
        height={36}
        sx={{
          flexShrink: 0,
          borderRadius: "8px",
          border: CARD_BORDER,
          ...skeletonWaveSx(SKELETON_BONE_FOOTER),
        }}
      />
    </Box>
  );
}

/**
 * Horizontal placeholders for the `/main` mobile floor-plan strip while
 * `GET /savedLayout/` is in flight.
 */
export function FloorPlanSliderSkeleton({ cardWidth, count = 3 }) {
  return (
    <Box
      aria-hidden
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch",
        gap: `${CARD_GAP_PX}px`,
        width: "100%",
        minWidth: 0,
        overflowX: "hidden",
        pl: { xs: SCROLL_INSET_XS, sm: SCROLL_INSET_SM },
        pr: { xs: SCROLL_INSET_XS, sm: SCROLL_INSET_SM },
        py: 0.5,
        boxSizing: "border-box",
      }}
    >
      {Array.from({ length: count }, (_, i) => (
        <Box
          key={i}
          sx={{
            flex: "0 0 auto",
            width: cardWidth,
            minWidth: 0,
            display: "flex",
            scrollSnapAlign: "start",
          }}
        >
          <FloorPlanCardSkeletonShell sx={{ width: "100%" }}>
            <SkeletonPreviewBlock />
            <SkeletonFooterBlock />
          </FloorPlanCardSkeletonShell>
        </Box>
      ))}
    </Box>
  );
}

FloorPlanSliderSkeleton.propTypes = {
  cardWidth: PropTypes.string.isRequired,
  count: PropTypes.number,
};

/**
 * Two-column placeholders for the `/main` bento floor-plan grid (md+).
 */
export function FloorPlanGridSkeleton({ tileCount = 4 }) {
  return (
    <Grid container spacing={2} alignItems="stretch" mt={0}>
      {Array.from({ length: tileCount }, (_, i) => (
        <Grid key={i} item md={6} sx={{ display: "flex", minWidth: 0 }}>
          <FloorPlanCardSkeletonShell>
            <SkeletonPreviewBlock />
            <SkeletonFooterBlock />
          </FloorPlanCardSkeletonShell>
        </Grid>
      ))}
    </Grid>
  );
}

FloorPlanGridSkeleton.propTypes = {
  tileCount: PropTypes.number,
};
